import React from 'react';
import Layout from '../components/layout/Layout';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
);

const PrivacyPage = () => {
  return (
    <Layout title="Privacy Policy" showBack>
      <div className="bg-white px-4 py-6" data-testid="privacy-page">
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-500 text-sm mb-6">Last updated: June 2026</p>

          <Section title="1. Introduction">
            <p className="text-gray-600 mb-3">
              Minted ("we", "our", or "us") operates the Minted marketplace at mintedbuysell.com.au.
              We are committed to protecting your privacy and handling your personal information in
              accordance with the <strong>Australian Privacy Act 1988 (Cth)</strong> and the Australian
              Privacy Principles (APPs). Where you access Minted from the European Economic Area or the
              United Kingdom, we also respect your rights under the GDPR / UK GDPR.
            </p>
            <p className="text-gray-600">
              By using Minted, you consent to the practices described in this Privacy Policy.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <h3 className="font-medium text-gray-800 mb-2">Information you provide:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-2 mb-4">
              <li><strong>Account information:</strong> name, email address, password (stored hashed), location</li>
              <li><strong>Profile information:</strong> profile photo, bio, store details</li>
              <li><strong>Listing information:</strong> photos, descriptions, prices and shipping details of items you sell</li>
              <li><strong>Transaction information:</strong> purchase history, shipping addresses, order records</li>
              <li><strong>Communications:</strong> messages with other users, support enquiries</li>
              <li><strong>Payment information:</strong> processed entirely by Stripe — we never receive or store your card number</li>
              <li><strong>Payout details:</strong> BSB and account number or PayPal email, used solely to pay you your sale proceeds</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">Information collected automatically:</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Device information:</strong> device type, operating system, browser type</li>
              <li><strong>Usage data:</strong> pages viewed, listings viewed, searches, features used</li>
              <li><strong>Approximate location:</strong> derived from your IP address, used to suggest your display currency and region</li>
              <li><strong>Cookies and local storage:</strong> to keep you signed in and remember preferences (see section 8)</li>
              <li><strong>Push notification tokens:</strong> only if you opt in to notifications</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>Create and manage your account</li>
              <li>Process purchases, hold and release seller funds, and process payouts</li>
              <li>Enable messaging between buyers and sellers (delivered in real time)</li>
              <li>Send transactional emails (order confirmations, receipts, shipping updates, password resets)</li>
              <li>Send push notifications and marketing emails — only with your consent, and you can opt out any time</li>
              <li>Generate shipping quotes and labels for your orders</li>
              <li>Display prices in your local currency (using live exchange rates)</li>
              <li>Detect and prevent fraud, abuse and security incidents</li>
              <li>Comply with legal obligations, including tax and consumer law</li>
            </ul>
          </Section>

          <Section title="4. Third Parties We Share Data With">
            <p className="text-gray-600 mb-3">
              We never sell your personal information. We share only what is necessary with these
              service providers to operate Minted:
            </p>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Stripe</strong> (payments) — receives your payment details directly; PCI-DSS Level 1 certified</li>
              <li><strong>Amazon Web Services (AWS S3, Sydney region)</strong> — stores listing photos and images</li>
              <li><strong>Resend</strong> — delivers our transactional and marketing emails</li>
              <li><strong>OneSignal</strong> — delivers push notifications you have opted into</li>
              <li><strong>Shippit / EasyPost</strong> — receive delivery name and address to generate shipping quotes and labels</li>
              <li><strong>Sellers you buy from</strong> — receive your delivery name and address to fulfil your order</li>
            </ul>
            <p className="text-gray-600 mt-3">
              Some providers store data outside Australia (e.g. the United States). We only use
              providers with appropriate security and contractual safeguards, as required by APP 8.
            </p>
          </Section>

          <Section title="5. Your Rights">
            <p className="text-gray-600 mb-3">You can, at any time:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Access &amp; export</strong> your personal data — use "Download My Data" on the Manage Data page</li>
              <li><strong>Correct</strong> your details — edit them in your Profile</li>
              <li><strong>Delete</strong> your account and data — via the Delete Account page, or request specific data deletion via Manage Data</li>
              <li><strong>Opt out</strong> of marketing emails (unsubscribe link) and push notifications (device settings)</li>
              <li><strong>Complain</strong> — contact us first; you may also complain to the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au</li>
            </ul>
            <p className="text-gray-600 mt-3">
              EEA/UK users additionally have GDPR rights to restriction, objection and data
              portability — the export and deletion tools above satisfy these, or contact us directly.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>Account data: kept while your account is active</li>
              <li>Transaction and payout records: kept up to 7 years as required by Australian tax law, even after account deletion</li>
              <li>Messages: kept while both accounts exist</li>
              <li>When you delete your account, personal data not legally required to be retained is removed</li>
            </ul>
          </Section>

          <Section title="7. Security">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>All traffic is encrypted with HTTPS/TLS</li>
              <li>Passwords are stored hashed, never in plain text</li>
              <li>Card details never touch our servers (handled by Stripe)</li>
              <li>Administrative access is authenticated and rate-limited</li>
            </ul>
            <p className="text-gray-600 mt-3">
              If a data breach occurs that is likely to result in serious harm, we will notify
              affected users and the OAIC in accordance with the Notifiable Data Breaches scheme.
            </p>
          </Section>

          <Section title="8. Cookies & Local Storage">
            <p className="text-gray-600 mb-3">We use a small number of cookies and browser local storage entries:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Essential:</strong> keeping you signed in, remembering your session and cart/checkout state</li>
              <li><strong>Preferences:</strong> your chosen display currency and dismissed banners</li>
              <li><strong>Notifications:</strong> OneSignal stores a token if you enable push notifications</li>
            </ul>
            <p className="text-gray-600 mt-3">
              We do not use third-party advertising or cross-site tracking cookies.
            </p>
          </Section>

          <Section title="9. Children">
            <p className="text-gray-600">
              Minted is not directed at children under 16. We do not knowingly collect personal
              information from children. Adult-category listings are gated behind an age confirmation.
            </p>
          </Section>

          <Section title="10. Changes & Contact">
            <p className="text-gray-600 mb-3">
              We may update this policy from time to time; the "Last updated" date above will change
              and material changes will be notified in-app.
            </p>
            <p className="text-gray-600">
              Questions or privacy requests: contact us via the in-app support chat or at
              admin@mintedbuysell.com.au.
            </p>
          </Section>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPage;
